import React, { Fragment, useMemo, useState } from 'react';
import { Combobox, Transition } from '@headlessui/react';
import {
  CheckIcon,
  ChevronUpDownIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

/**
 * Select com múltipla seleção, busca e chips.
 *
 * Os valores trafegam sempre como string para não misturar os ids numéricos
 * (accounts, inboxes) com os bigint que o Postgres devolve como texto (teams).
 *
 * @param {Array<{value: string, label: string, hint?: string, group?: string}>} options
 * @param {string[]} value  ids selecionados
 */
const MultiSelect = ({
  label,
  options = [],
  value = [],
  onChange,
  placeholder = 'Todos',
  disabled = false,
  loading = false,
  emptyMessage = 'Nenhuma opção disponível',
  helpText,
}) => {
  const [query, setQuery] = useState('');

  const isDisabled = disabled || loading || options.length === 0;

  const filteredOptions = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return options;

    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(term) ||
        (option.hint || '').toLowerCase().includes(term) ||
        (option.group || '').toLowerCase().includes(term)
    );
  }, [options, query]);

  const selectedOptions = useMemo(
    () => options.filter((option) => value.includes(option.value)),
    [options, value]
  );

  const buttonLabel = () => {
    if (loading) return 'Carregando...';
    if (options.length === 0) return emptyMessage;
    if (selectedOptions.length === 0) return placeholder;
    if (selectedOptions.length === 1) return selectedOptions[0].label;
    return `${selectedOptions.length} selecionados`;
  };

  const selectAll = () => onChange(filteredOptions.map((option) => option.value));
  const clearAll = () => onChange([]);
  const removeOne = (optionValue) => onChange(value.filter((item) => item !== optionValue));

  return (
    <div className="min-w-[13rem] flex-1">
      <div className="flex items-center justify-between mb-1">
        <label className="block text-sm font-medium text-gray-700">{label}</label>
        {value.length > 0 && (
          <button
            type="button"
            onClick={clearAll}
            className="text-xs text-gray-500 hover:text-red-600 transition-colors"
          >
            limpar
          </button>
        )}
      </div>

      <Combobox value={value} onChange={onChange} multiple disabled={isDisabled}>
        <div className="relative">
          <Combobox.Button
            className={`relative w-full cursor-pointer rounded-md border py-2 pl-3 pr-10 text-left text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              isDisabled
                ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                : 'border-gray-300 bg-white hover:border-blue-400'
            }`}
          >
            <span className={`block truncate ${value.length === 0 ? 'text-gray-500' : 'text-gray-900 font-medium'}`}>
              {buttonLabel()}
            </span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
            </span>
          </Combobox.Button>

          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery('')}
          >
            <Combobox.Options className="absolute z-50 mt-1 max-h-72 w-full min-w-[16rem] overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
              <div className="sticky top-0 z-10 bg-white px-2 pb-2 pt-1 border-b border-gray-100">
                <div className="relative">
                  <MagnifyingGlassIcon className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                  <Combobox.Input
                    className="w-full rounded border border-gray-200 py-1.5 pl-8 pr-2 text-sm focus:border-blue-400 focus:outline-none"
                    placeholder="Buscar..."
                    displayValue={() => query}
                    onChange={(event) => setQuery(event.target.value)}
                  />
                </div>
                <div className="mt-2 flex items-center justify-between text-xs">
                  <button
                    type="button"
                    onClick={selectAll}
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Selecionar {query ? 'filtrados' : 'todos'} ({filteredOptions.length})
                  </button>
                  <button type="button" onClick={clearAll} className="text-gray-500 hover:text-red-600">
                    Limpar seleção
                  </button>
                </div>
              </div>

              {filteredOptions.length === 0 ? (
                <div className="px-3 py-4 text-center text-gray-500">Nenhum resultado</div>
              ) : (
                filteredOptions.map((option) => (
                  <Combobox.Option
                    key={option.value}
                    value={option.value}
                    className={({ active }) =>
                      `relative cursor-pointer select-none py-2 pl-9 pr-3 ${
                        active ? 'bg-blue-50 text-blue-900' : 'text-gray-900'
                      }`
                    }
                  >
                    {({ selected }) => (
                      <>
                        {selected && (
                          <span className="absolute inset-y-0 left-0 flex items-center pl-2.5 text-blue-600">
                            <CheckIcon className="h-4 w-4" aria-hidden="true" />
                          </span>
                        )}
                        <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                          {option.label}
                        </span>
                        {(option.hint || option.group) && (
                          <span className="block truncate text-xs text-gray-500">
                            {[option.group, option.hint].filter(Boolean).join(' · ')}
                          </span>
                        )}
                      </>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>

      {helpText && <p className="mt-1 text-xs text-gray-500">{helpText}</p>}

      {selectedOptions.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {selectedOptions.slice(0, 4).map((option) => (
            <span
              key={option.value}
              className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700"
            >
              <span className="max-w-[10rem] truncate">{option.label}</span>
              <button
                type="button"
                onClick={() => removeOne(option.value)}
                className="text-blue-500 hover:text-blue-800"
                aria-label={`Remover ${option.label}`}
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            </span>
          ))}
          {selectedOptions.length > 4 && (
            <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
              +{selectedOptions.length - 4}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelect;
